import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import { API_URL } from '../config';
import { getAuthToken, updateJob } from '../services/api';

// Link / change / unlink this job's parent job. Reuses the job-search endpoint the
// Add/Edit Job form uses, and writes parent_job_id via the standard job update.
export default function ParentJobModal({ jobId, parentId, parentNumber, onClose, onSaved }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const token = await getAuthToken();
        const cfg = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
        const res = await axios.get(`${API_URL}/jobs?search=${encodeURIComponent(term)}&limit=10`, cfg);
        if (active) setResults((res.data.jobs || []).filter(j => j.id !== Number(jobId) && j.id !== parentId));
      } catch (e) { /* ignore search errors */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [search, jobId, parentId]);

  const save = async (newParentId) => {
    setSaving(true); setError('');
    try {
      await updateJob(jobId, { parent_job_id: newParentId });
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not update the parent job.');
      setSaving(false);
    }
  };

  return (
    <div className="qv-modal-overlay" onClick={onClose}>
      <div className="qv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qv-modal-head">
          <h3>Parent Job</h3>
          <button className="btn-ghost" onClick={onClose}><FiX /> Close</button>
        </div>
        <div className="qv-modal-body">
          {parentId ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 14 }}>Linked to <strong>{parentNumber || `#${parentId}`}</strong></span>
              <button className="btn btn-secondary" disabled={saving} onClick={() => save(null)}>Unlink</button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>No parent job linked yet. Search below to link one.</div>
          )}
          <div className="qv-field qv-field-wide">
            <label>Search jobs by number or address</label>
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. J4690" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {results.map(j => (
              <button
                key={j.id}
                disabled={saving}
                onClick={() => save(j.id)}
                style={{ textAlign: 'left', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: saving ? 'wait' : 'pointer' }}
              >
                <div style={{ fontWeight: 600, color: '#2563eb' }}>{j.job_number}</div>
                {(j.property_address || j.client_name) && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{[j.client_name, j.property_address].filter(Boolean).join(' · ')}</div>
                )}
              </button>
            ))}
            {search.trim().length >= 2 && results.length === 0 && (
              <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 2px' }}>No matching jobs.</div>
            )}
          </div>
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', padding: '0 1.5rem' }}>{error}</div>}
        <div className="qv-modal-foot">
          <button className="btn btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
